/**
 * Macro source graph: the authored topology. Nodes reference input slices and screen-state
 * regions of immutable raw packages; the compiler turns it into a compiled package.
 */
import { z } from 'zod';
import { macroForkSchema, macroGroupSchema, macroTreeEdgeSchema, macroTriggerSchema } from '../macroTree';
import { sha256HexSchema } from './canonicalJson';
import { macroInputCoordinatePolicySchema, macroInputModePolicyOverrideSchema, macroInputModePolicySchema } from './inputModePolicy';
import { rawInputEventSchema } from './rawInputEvent';
import { rawRegionRectSchema } from './rawPackage';
import { authoredMatchRegionSchema, recordingPinSchema } from './authoringPrimitives';
import { macroMatchingModeSchema } from './matchingMode';

export const inputEditSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('delete'), packageId: z.string().uuid(), seq: z.number().int().nonnegative().safe() }).strict(),
  z.object({ op: z.literal('shift'), packageId: z.string().uuid(), seq: z.number().int().nonnegative().safe(), deltaUs: z.number().int().safe() }).strict(),
  z.object({ op: z.literal('insert'), afterUs: z.number().int().nonnegative().safe(), event: rawInputEventSchema }).strict(),
]);
export type InputEdit = z.infer<typeof inputEditSchema>;

export const macroSourceNodeInputSchema = z.object({ packageId: z.string().uuid(), fromUs: z.number().int().nonnegative().safe(),
  toUs: z.number().int().nonnegative().safe(), edits: z.array(inputEditSchema).max(10_000),
  policyOverride: macroInputModePolicyOverrideSchema.optional() }).strict();
export type MacroSourceNodeInput = z.infer<typeof macroSourceNodeInputSchema>;

export const macroSourceExpectedStateSchema = z.object({
  regions: z.array(z.object({ packageId: z.string().uuid(), regionId: z.string().uuid(),
    /** Overrides the raw index rect; raw packages are immutable. */
    rect: rawRegionRectSchema.optional(),
    matchThresholdPercent: z.number().int().min(0).max(100).optional() }).strict()).min(1).max(64),
  matchThresholdPercent: z.number().int().min(0).max(100).optional() }).strict();
export type MacroSourceExpectedState = z.infer<typeof macroSourceExpectedStateSchema>;

export const macroSourceNodeSchema = macroGroupSchema.omit({ macros: true, actionTimeline: true, expectedState: true }).extend({
  input: macroSourceNodeInputSchema.nullable(),
  coordinates: macroInputCoordinatePolicySchema.optional(),
  expectedState: macroSourceExpectedStateSchema.optional(),
}).strict();
export type MacroSourceNode = z.infer<typeof macroSourceNodeSchema>;

export const macroSourceGraphSchema = z.object({ format: z.literal('playcast.macro.source'), formatVersion: z.literal(1),
  campaignId: z.string().min(1).max(128), trigger: macroTriggerSchema, rootNodeId: z.string().uuid(),
  nodes: z.array(macroSourceNodeSchema).min(1).max(4_096), edges: z.array(macroTreeEdgeSchema),
  packages: z.array(z.object({ packageId: z.string().uuid(), manifestSha256: sha256HexSchema }).strict()).min(1).max(64),
  policy: macroInputModePolicySchema, matchingMode: macroMatchingModeSchema.optional(),
  metadata: z.object({ forks: z.array(macroForkSchema).optional() }).strict() }).strict();
export type MacroSourceGraph = z.infer<typeof macroSourceGraphSchema>;

/** Gated v2 path: authored regions resolve native interaction tags, not mutable raw-region rows. */
export const macroAuthoringSourceExpectedStateSchema = z.object({
  regions: z.array(authoredMatchRegionSchema).min(1).max(64),
  matchThresholdPercent: z.number().int().min(0).max(100).optional(),
}).strict();
export const macroAuthoringSourceNodeSchema = macroSourceNodeSchema.extend({
  expectedState: macroAuthoringSourceExpectedStateSchema.optional(),
}).strict();
export type MacroAuthoringSourceNode = z.infer<typeof macroAuthoringSourceNodeSchema>;
export const macroAuthoringSourceGraphSchema = macroSourceGraphSchema.extend({
  formatVersion: z.literal(2, { errorMap: () => ({ message: 'legacy_macro_requires_rerecording: source v2 is required' }) }),
  nodes: z.array(macroAuthoringSourceNodeSchema).min(1).max(4096),
  packages: z.array(recordingPinSchema).min(1).max(64),
  metadata: z.object({ forks: z.array(macroForkSchema).optional(), macroId: z.string().uuid().optional(),
    authoringDigest: sha256HexSchema.optional(),
    provenance: z.array(z.object({ instanceId: z.string().uuid(), macroId: z.string().uuid(),
      nodeIds: z.array(z.string().uuid()).min(1) }).strict()).optional(),
  }).strict(),
}).strict().superRefine((graph, context) => {
  const packageIds = new Set(graph.packages.map(pin => pin.packageId));
  const nodeIds = new Set(graph.nodes.map(node => node.id));
  if (packageIds.size !== graph.packages.length || nodeIds.size !== graph.nodes.length || !nodeIds.has(graph.rootNodeId)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Source identities must be unique and root must exist' });
  }
  for (const node of graph.nodes) {
    if (node.input && (!packageIds.has(node.input.packageId) || node.input.fromUs >= node.input.toUs)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Input slice must be nonempty and pinned' });
    }
    if (node.input?.edits.some(edit => edit.op !== 'insert' && edit.packageId !== node.input!.packageId)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Node input edits must target its pinned recording' });
    }
    const regions = node.expectedState?.regions ?? [];
    if (regions.some(region => !packageIds.has(region.packageId))
      || new Set(regions.map(region => region.regionId)).size !== regions.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Authored regions must be distinct and pinned' });
    }
  }
  if (graph.edges.some(edge => !nodeIds.has(edge.source) || !nodeIds.has(edge.target))) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Source edges must resolve existing nodes' });
  }
  if (new Set(graph.edges.map(edge => edge.id)).size !== graph.edges.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Source edge identities must be unique' });
  }
  const owners = graph.metadata.provenance ?? [];
  const ownedNodeIds = owners.flatMap(owner => owner.nodeIds);
  if (new Set(owners.map(owner => owner.instanceId)).size !== owners.length
    || new Set(ownedNodeIds).size !== ownedNodeIds.length || ownedNodeIds.some(id => !nodeIds.has(id))) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Publication provenance must name distinct instances and existing exclusively owned nodes' });
  }
});
export type MacroAuthoringSourceGraph = z.infer<typeof macroAuthoringSourceGraphSchema>;
